import {
  MitoVault,
  MitoSubscription,
  MitoPriceSnapshot
} from '@injectivelabs/sdk-ts'
import { BigNumberInBase } from '@injectivelabs/utils'
import {
  sharedToBalanceInWei,
  sharedToBalanceInToken
} from '@shared/utils/formatter'
import {
  SharedMarketType,
  SharedBalanceInUsdWithTokenAndPrice
} from '@shared/types'
import { IS_STAGING } from '@/app/utils/constant'
import {
  getLpDenom,
  getVaultName,
  getDefaultTokenMeta
} from '@/app/client/utils/mito'
import { vaultTvlOverrides } from '@/app/data/vault'
import {
  UiVault,
  UiMarket,
  VaultType,
  UiSubscription,
  UIPriceSnapShot,
  VaultMarketType,
  RegistrationMode,
  VaultCategoryType,
  VaultContractType
} from '@/types'

const calculateLpTokenUsdPrice = ({
  vault,
  market,
  quoteUsdValue
}: {
  vault: MitoVault
  market: UiMarket
  quoteUsdValue: number
}) => {
  const baseAmountInWei =
    vault.subaccountInfo?.balancesList.find(
      ({ denom }) => denom === market.baseToken.denom
    )?.totalBalance || '0'

  const quoteAmountInWei =
    vault.subaccountInfo?.balancesList.find(
      ({ denom }) => denom === market.quoteToken.denom
    )?.totalBalance || '0'

  const totalInQuote = new BigNumberInBase(quoteAmountInWei)
    .dividedBy(baseAmountInWei)
    .multipliedBy(baseAmountInWei)
    .plus(quoteAmountInWei)
    .toFixed(0)

  const totalInUsd = new BigNumberInBase(totalInQuote).multipliedBy(
    quoteUsdValue
  )
  const lpTokenPrice = totalInUsd.dividedBy(vault.totalLpAmount)

  return lpTokenPrice.isNaN() ? 0 : lpTokenPrice.toNumber()
}

export class UiMitoTransformer {
  static vaultToUiVault(vault: MitoVault): UiVault | undefined {
    const appStore = useAppStore()
    const spotStore = useSpotStore()
    const tokenStore = useTokenStore()
    const derivativeStore = useDerivativeStore()

    const market = [...spotStore.markets, ...derivativeStore.markets].find(
      (market: UiMarket) => market.marketId === vault.marketId
    )

    if (!market) {
      return
    }

    const typeKey =
      Object.keys(VaultContractType)[
        Object.values(VaultContractType).indexOf(
          vault.vaultType as VaultContractType
        )
      ]

    if (!typeKey) {
      return
    }

    const vaultType = vault.vaultType as VaultContractType
    const isPermissionless =
      vault.registrationMode === RegistrationMode.permissionless
    const formattedVaultName = getVaultName({
      vault,
      market,
      vaultType
    })

    const vaultTokenMeta = getDefaultTokenMeta({
      market,
      denom: getLpDenom(vault),
      vaultName: formattedVaultName,
      isOffChain: vaultType === VaultContractType.OffChain
    })

    const lpTokenPrice = sharedToBalanceInWei({
      value: vault.lpTokenPrice,
      decimalPlaces: vaultTokenMeta.decimals
    }).toNumber()

    const uiLpTokenPrice = calculateLpTokenUsdPrice({
      vault,
      market,
      quoteUsdValue: tokenStore.tokenUsdPrice(market.quoteToken)
    })

    tokenStore.appendMitoLpToken(vaultTokenMeta)

    const currentTvl = appStore.hasAdminPrivileges
      ? vault.currentTvl
      : vaultTvlOverrides[vault.contractAddress] || vault.currentTvl

    let apy = IS_STAGING ? vault.apyue : vault.apy7D

    // hide negative performance
    apy = appStore.devMode ? apy : new BigNumberInBase(apy).lte(0) ? 0 : apy

    return {
      ...vault,
      apy,
      market,
      vaultType,
      currentTvl,
      uiLpTokenPrice,
      isPermissionless,
      token: vaultTokenMeta,
      vaultName: formattedVaultName,
      label: isPermissionless
        ? vault.vaultName.replace('permissionless_vault_', 'Vault #')
        : '',
      lpDenom: getLpDenom(vault),
      type: VaultType[typeKey as VaultType],
      category:
        vaultType === VaultContractType.OffChain
          ? VaultCategoryType.Managed
          : VaultCategoryType.Automated,
      marketType:
        market.type === SharedMarketType.Spot
          ? VaultMarketType.Spot
          : VaultMarketType.Derivative,
      lpTokenPrice: new BigNumberInBase(lpTokenPrice).gt(0)
        ? lpTokenPrice
        : uiLpTokenPrice,
      subaccountId: vault.subaccountInfo?.subaccountId || ''
    }
  }

  static subscriptionToUiSubscription(
    subscription: MitoSubscription
  ): UiSubscription | undefined {
    if (!subscription.vaultInfo) {
      return
    }

    const vault = UiMitoTransformer.vaultToUiVault(subscription.vaultInfo)

    if (!vault) {
      return
    }

    const usdPrice = sharedToBalanceInWei({
      decimalPlaces: vault.token.decimals,
      value: subscription.vaultInfo.lpTokenPrice
    }).toNumber()

    const balanceInUsd = new BigNumberInBase(
      sharedToBalanceInToken({
        value: subscription.lpAmount,
        decimalPlaces: vault.token.decimals
      })
    )
      .multipliedBy(usdPrice)
      .toFixed()

    const balanceWithTokenAndPrice = {
      usdPrice,
      balanceInUsd,
      token: vault.token,
      balance: subscription.lpAmount,
      denom: getLpDenom(subscription.vaultInfo)
    } as SharedBalanceInUsdWithTokenAndPrice

    return {
      ...subscription,
      balanceWithTokenAndPrice,
      vaultInfo: UiMitoTransformer.vaultToUiVault(
        subscription.vaultInfo
      ) as UiVault
    }
  }

  static tokenPriceSnapshotToUiPriceSnapshot(
    priceSnapshot: MitoPriceSnapshot
  ): UIPriceSnapShot {
    return {
      updatedAt: Number(priceSnapshot.updatedAt),
      price: sharedToBalanceInWei({
        value: priceSnapshot.price
      }).toNumber()
    }
  }

  static tvlPriceSnapshotToUiPriceSnapshot(
    priceSnapshot: MitoPriceSnapshot
  ): UIPriceSnapShot {
    return {
      ...priceSnapshot,
      updatedAt: Number(priceSnapshot.updatedAt)
    }
  }
}
